/**
 * Direct-to-storage byte upload for the ingesta flow.
 *
 * `signUploads` returns an OPAQUE `upload_url` (cloud: GCS v4 signed PUT URL;
 * local: `PUT /_local/storage/{object_name}` — excluded from the generated
 * client). We PUT the File bytes verbatim to that URL. No Authorization header:
 * the URL is pre-signed (cloud) or an env-gated local route, and the generated
 * client's auth middleware does not apply to this raw request.
 *
 * `fetch` cannot report upload progress, so we use XMLHttpRequest to surface a
 * per-file percentage to the operator.
 */

import { IS_DEMO } from "../../api/client";

export type PutProgress = (pct: number) => void;

/**
 * Resolves on a 2xx; rejects with a Spanish Error otherwise.
 *
 * `contentType` MUST be the exact value sent to `signUploads` for this file: a
 * cloud GCS v4 signed PUT URL signs the `Content-Type` header, so any mismatch
 * (or omitting it) yields a 403 SignatureDoesNotMatch. We always send it.
 */
export function putBytes(
  uploadUrl: string,
  file: File,
  contentType: string,
  onProgress?: PutProgress,
  signal?: AbortSignal,
): Promise<void> {
  // DEMO mode: no network — simulate a short, smooth upload.
  if (IS_DEMO) {
    return simulateUpload(onProgress, signal);
  }

  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", uploadUrl, true);
    // Must match the Content-Type that was signed (see jsdoc above).
    xhr.setRequestHeader("Content-Type", contentType);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(100);
        resolve();
      } else {
        reject(
          new Error(
            `La carga falló (HTTP ${xhr.status}). Verifica el archivo e inténtalo de nuevo.`,
          ),
        );
      }
    };
    xhr.onerror = () =>
      reject(new Error("No se pudo conectar con el almacenamiento. Revisa tu conexión."));
    xhr.onabort = () => reject(new Error("Carga cancelada."));

    if (signal) {
      if (signal.aborted) {
        // Reject here: xhr.abort() before send() fires NO abort event, so
        // relying on onabort would leave this promise pending forever.
        reject(new Error("Carga cancelada."));
        return;
      }
      signal.addEventListener("abort", () => xhr.abort(), { once: true });
    }

    xhr.send(file);
  });
}

function simulateUpload(onProgress?: PutProgress, signal?: AbortSignal): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    let pct = 0;
    const id = setInterval(() => {
      if (signal?.aborted) {
        clearInterval(id);
        reject(new Error("Carga cancelada."));
        return;
      }
      pct = Math.min(100, pct + 20);
      onProgress?.(pct);
      if (pct >= 100) {
        clearInterval(id);
        resolve();
      }
    }, 120);
  });
}
